import React, { useEffect, useMemo, useRef, useState } from "react";
import { Step, StepOption } from "../../types/wizard";
import { IMAGE_SERVER } from "../../config";
import {
  resetValidations,
  saveEmblemData,
  setStepCallback,
  startFromProductType,
  validateWizardSteps,
  wizardStore,
} from "../../stores/wizard";
import { useSnapshot } from "valtio";
import useOutsideClick from "../../hooks/useOutsideClick";
import { useProductRules } from "../../hooks/useProductRules";
import { Steps } from "../../constants";
import "./../QuickView/QuickView.css";
import mapProductType from "../../config/productMapping";

interface DropdownProps {
  step: Step;
}

const DetailedSelectorSkeleton: React.FC = () => {
  return (
    <div className="w-full">
      <div className="animate-pulse h-6 bg-gray-300 rounded w-28 mb-2.5"></div>
      <div className="p-2.5 bg-white border border-gray-300 rounded animate-pulse">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-gray-300 rounded"></div>
            <div className="h-6 bg-gray-300 rounded w-32"></div>
          </div>
          <div className="w-4 h-4 bg-gray-300 rounded"></div>
        </div>
      </div>
    </div>
  );
};

const ImageWithSkeleton: React.FC<{
  src: string;
  alt: string;
  className?: string;
}> = ({ src, alt, className }) => {
  const [isLoading, setIsLoading] = useState(true);

  return (
    <div
      className={`relative ${className}`}
      style={{ width: "2rem", height: "2rem" }}
    >
      {isLoading && (
        <div className="rounded-full object-cover bg-slate-200 w-8 h-8 animate-pulse" />
      )}
      <img
        src={src}
        alt={alt}
        className={`rounded object-cover w-8 h-8 ${isLoading ? "opacity-0" : "opacity-100"
          }`}
        onLoad={() => setIsLoading(false)}
      />
    </div>
  );
};

const PreselectedStepCard: React.FC<{
  step: Step;
  selectedOption: StepOption;
  getStepLabel: (stepId: string) => string;
}> = ({ step, selectedOption, getStepLabel }) => {
  const getBorderColor = (htmlColor: string) => {
    return !!(htmlColor && htmlColor === "#FFFFFF")
      ? "1px solid #a7acaf"
      : "none";
  };

  return (
    <div className="relative bg-white border-2 border-orange-300 rounded-lg p-3">
      <div className="flex items-center space-x-3">
        <div className="flex-shrink-0">
          {selectedOption.thumbnailImage?.url ? (
            <img
              src={`${IMAGE_SERVER}${selectedOption.thumbnailImage?.url}`}
              alt={selectedOption.name}
              className="object-cover rounded w-12 h-12"
            />
          ) : selectedOption?.htmlColor ? (
            <div
              className="w-12 h-12 object-cover rounded"
              style={{
                background: selectedOption.htmlColor,
                border: getBorderColor(selectedOption.htmlColor),
              }}
            />
          ) : (
            <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
              <span className="text-gray-500 text-xs">IMG</span>
            </div>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">
            {getStepLabel(step.id)}
          </div>
          <div className="text-base font-semibold text-gray-800 truncate">
            {selectedOption.name}
          </div>
          {step.disclaimer && (
            <div className="text-xs text-gray-600 mt-1">
              <span
                dangerouslySetInnerHTML={{ __html: step.disclaimer.message }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Selector: React.FC<DropdownProps> = ({ step }) => {
  const wizSnap = useSnapshot(wizardStore);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<StepOption | null>(null);
  const [isProductTypeSelected, setIsProductTypeSelected] = useState(false);
  const [searchText, setSearchText] = useState<string>("");
  const selectRef = useRef(null);
  const isInvalid = wizSnap.errors[step?.id]?.length;
  const border = isInvalid
    ? "border border-solid border-[#a61613]"
    : "border border-solid border-[#ccc]";

  // Get product rules
  const { isReadOnly, defaultOption, excludedOptionCodes } = useProductRules(
    step?.id,
    step?.options
  );

  const urlParams = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    return {
      productType:
        searchParams.get("productType") ||
        searchParams.get("ProductType") ||
        null,
      productSubtype:
        searchParams.get("productSubtype") ||
        searchParams.get("ProductSubtype") ||
        null,
    };
  }, []);

  const [matchedOptions, _] = useState(() => {
    const paramMap: { [key: string]: string } = {
      productType: Steps.ProductType,
      productSubtype: Steps.ProductSubtype,
    };
    const matches: { [key: string]: string | null } = {};
    Object.entries(urlParams).forEach(([param, value]) => {
      if (value && paramMap[param]) {
        matches[paramMap[param]] = value;
      }
    });
    return matches;
  });

  const getStepLabel = (stepId: string): string => {
    switch (stepId) {
      case Steps.ProductType:
        return "Product Type";
      case Steps.ProductSubtype:
        return "Product Subtype";
      default:
        return step.title || "Step";
    }
  };

  useOutsideClick([selectRef], () => {
    setIsOpen(false);
  });

  // Single effect to handle option selection and product rules
  useEffect(() => {
    if (!step?.id || !step.options) return;

    if (isReadOnly && defaultOption) {
      const currentSelection = wizSnap.emblemData[step.id];

      if (!currentSelection || currentSelection.id !== defaultOption.id) {
        handleSelect(defaultOption);
      } else if (!selectedOption || selectedOption.id !== defaultOption.id) {
        setSelectedOption(defaultOption);
      }
      return;
    }

    const savedOption = wizSnap.emblemData[step.id] || null;

    if (savedOption) {
      const optionStillExists = step.options.some(
        (opt) => opt.id === savedOption.id ||
          opt.code?.toLowerCase() === savedOption.code?.toLowerCase()
      );

      if (optionStillExists) {
        setSelectedOption(savedOption);
        setIsProductTypeSelected(true);
      } else {
        saveEmblemData(step.id, null);
        setSelectedOption(null);
      }
      return;
    }

    const productTypeOptions = wizSnap.steps?.find(
      (s) => s.id === Steps.ProductType
    )?.options;
    const productSubtypeOptions = wizSnap.steps?.find(
      (s) => s.id === Steps.ProductSubtype
    )?.options;

    const matchedProduct: StepOption | null = mapProductType(
      matchedOptions[Steps.ProductType],
      productTypeOptions
    );

    const productSubtypeMatch = productSubtypeOptions?.find(
      (opt) =>
        opt.code?.toLowerCase() ===
        matchedOptions[Steps.ProductSubtype]?.toLowerCase()
    );

    if (
      !isProductTypeSelected &&
      matchedProduct &&
      step?.id === Steps.ProductType &&
      matchedOptions[Steps.ProductType]
    ) {
      handleSelect(matchedProduct);
      setIsProductTypeSelected(true);
      return;
    }

    if (
      (isProductTypeSelected || matchedProduct) &&
      productSubtypeMatch &&
      step?.id === Steps.ProductSubtype &&
      matchedOptions[Steps.ProductSubtype]
    ) {
      handleSelect(productSubtypeMatch);
      return;
    }

    if (step.options.length === 1) {
      handleSelect(step.options[0]);
      setIsProductTypeSelected(true);
      return;
    }

    // Reset selectedOption to null if no saved option exists
    setSelectedOption(null);
  }, [wizSnap.steps, step?.id, step?.options, isReadOnly, defaultOption]);

  const handleSelect = (option: StepOption) => {
    if (!step?.id) return;

    setSearchText("");
    setSelectedOption(option);

    if (step.id === Steps.ProductType) {
      // Reset ProductSubtype when ProductType changes
      saveEmblemData(Steps.ProductSubtype, null);
      startFromProductType(step.id, option);
      resetValidations();
    } else {
      saveEmblemData(step.id, option);
    }

    const updatedStepContent = {
      ...wizSnap.emblemData[step.id],
      ...option,
    };

    const setCallback = setStepCallback[step.id] as (
      content: StepOption,
      stepId: string
    ) => void;
    if (setCallback) {
      setCallback(updatedStepContent, step.id);
      validateWizardSteps(step.id);
    } else {
      console.warn(`No callback defined for step: ${step.id}`);
    }

    setIsOpen(false);
  };

  const handleDropdownClick = () => {
    if (isReadOnly) return;
    if (!matchedOptions[step?.id]) {
      setIsOpen(!isOpen);
    }
  };

  // Search filter
  const filteredOptions = useMemo(() => {
    let options = step.options ?? [];

    if (excludedOptionCodes.length > 0) {
      options = options.filter(
        (opt) => !excludedOptionCodes.includes(opt.code ?? "")
      );
    }

    if (searchText) {
      options = options.filter((option) =>
        option.name.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    return options;
  }, [step.options, excludedOptionCodes, searchText]);

  const getBorderColor = (htmlColor: string) => {
    return !!(htmlColor && htmlColor === "#FFFFFF")
      ? "1px solid #a7acaf"
      : "none";
  };

  if (wizSnap.loading) {
    return <DetailedSelectorSkeleton />;
  }

  const isClickable = step?.id && !matchedOptions[step.id] && !isReadOnly;
  const isPreselected = step?.id && matchedOptions[step.id] && selectedOption;

  const getDropdownClasses = () => {
    const baseClasses = "p-2.5 relative rounded";

    if (isReadOnly) {
      return `${baseClasses} bg-gray-100 border border-solid border-[#ccc]`;
    }

    if (isClickable) {
      return `${baseClasses} bg-white ${border} cursor-pointer`;
    }

    return `${baseClasses} bg-white`;
  };

  if (isPreselected) {
    return (
      <div className="w-full">
        <PreselectedStepCard
          step={step}
          selectedOption={selectedOption}
          getStepLabel={getStepLabel}
        />
      </div>
    );
  }

  return (
    <div className="w-full" ref={selectRef}>
      <label className="block uppercase font-bold mb-2.5">
        {step.title?.replace(/color/gi, "colour")}
      </label>
      {step.disclaimer && (
        <div className="flex flex-row pb-4 w-full">
          <span
            dangerouslySetInnerHTML={{ __html: step.disclaimer.message }}
          ></span>
        </div>
      )}
      <div className={getDropdownClasses()} onClick={handleDropdownClick}>
        {selectedOption ? (
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {selectedOption.thumbnailImage?.url ? (
              <img
                src={`${IMAGE_SERVER}${selectedOption.thumbnailImage?.url}`}
                alt={selectedOption.name}
                className={`object-cover rounded ${isClickable || isReadOnly ? "w-8 h-8" : "w-10 h-10"
                  }`}
              />
            ) : (
              selectedOption?.htmlColor && (
                <div
                  className="object-cover rounded w-8 h-8"
                  style={{
                    background: selectedOption.htmlColor,
                    border: getBorderColor(selectedOption.htmlColor),
                  }}
                ></div>
              )
            )}
            <span className={`${!isClickable && !isReadOnly && "text-[1.2em]"}`}>
              {selectedOption.name}
            </span>
          </div>
        ) : (
          <div className="flex flex-row justify-between items-center">
            <span style={{ display: "flex" }}>-- Select a Value --</span>
            {!isReadOnly && <div className="arrow"></div>}
          </div>
        )}
      </div>

      {isInvalid && (
        <div className="text-[#a61613] text-sm leading-4 mt-2">
          <span>{wizSnap.errors[step?.id][0]}</span>
        </div>
      )}

      {isOpen && !isReadOnly && (
        <div className="relative w-full">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search"
            className="p-2 w-full border-b-2 border-gray-300 rounded outline-none"
            style={{
              border: "1px solid #ccc",
            }}
          />
          <ul
            className="m-0 p-0 rounded bg-white overflow-y-auto"
            style={{
              listStyle: "none",
              border: "1px solid #ccc",
              maxHeight: "200px",
              width: "inherit",
              zIndex: 1,
              display: filteredOptions?.length === 0 ? "none" : "inherit",
            }}
          >
            {filteredOptions?.map((option) => (
              <li
                key={option.id}
                onClick={() => handleSelect(option)}
                className="flex items-center p-2 cursor-pointer"
                style={{
                  gap: "10px",
                  borderBottom: "1px solid #eee",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                {option.thumbnailImage?.url ? (
                  <ImageWithSkeleton
                    src={`${IMAGE_SERVER}${option.thumbnailImage.url}`}
                    alt={option.name}
                    className="w-8 h-8"
                  />
                ) : (
                  option?.htmlColor && (
                    <div
                      className="object-cover rounded w-8 h-8"
                      style={{
                        background: option.htmlColor,
                        border: getBorderColor(option.htmlColor),
                      }}
                    ></div>
                  )
                )}
                <span>{option.name}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Selector;